/**
 * ค่าคงที่ของ JSON-LD ที่ content วางเองในคอนเทนต์
 *
 * ฝั่งที่เอาไปใช้จริงคือ `app-storefront` — มันอ่าน block ออกจากคอนเทนต์ด้วย
 * `extractJsonLdFromHtml()` แล้วปล่อยผ่าน `head()` (ดู `docs/features/06-static-page-jsonld-field.md`)
 * ไฟล์นี้มีไว้เพื่อ **เตือนตอนกรอก** ไม่ใช่ตัวบังคับสุดท้าย
 */

/**
 * `@type` ที่ห้าม content ประกาศเอง
 *
 * ⚠️ **ต้องตรงกับ `JSON_LD_CONTENT_BLOCKED_TYPES` ใน `app-storefront/enums/jsonLdId.js`**
 * ถ้าฝั่งนั้นเพิ่ม/ลด ต้องมาแก้ที่นี่ด้วย ไม่งั้น BO จะบอกว่าใช้ได้แต่ storefront ตัดทิ้งเงียบ
 * (สองรีโปคนละ repo จึงแชร์ constant กันไม่ได้ — ยอมรับว่าซ้ำ แต่ต้องซ้ำแบบรู้ตัว)
 *
 * เหตุผลที่ห้าม: หน้าปล่อย node เหล่านี้ให้อยู่แล้วและ `@id` ผูกกับ URL ของหน้า
 * ถ้า content วางซ้ำจะได้สอง node ที่ `@id` เดียวกันแต่เนื้อไม่ตรงกัน schema ของทั้งหน้าจะเพี้ยน
 */
export const JSON_LD_BLOCKED_TYPES = Object.freeze([
	'WebPage',
	'CollectionPage',
	'ContactPage',
	'ItemPage',
	'AboutPage',
	'Article',
	'BreadcrumbList',
	'WebSite',
	'Organization',
]);

export const JSON_LD_TEMPLATE = Object.freeze({
	ITEM_LIST: 'ItemList',
	FAQ_PAGE: 'FAQPage',
	HOW_TO: 'HowTo',
	VIDEO_OBJECT: 'VideoObject',
	EVENT: 'Event',
	FREE_FORM: 'freeForm',
});

/**
 * โครงของแต่ละ template — เก็บเป็น object ไม่ใช่ string
 *
 * ตั้งใจไม่เก็บเป็น template literal เพราะ JSON ที่เขียนมือในโค้ดพังง่าย
 * (comma เกิน, quote ไม่ปิด) และ indent ในไฟล์จะกลายเป็นส่วนหนึ่งของ string ไปด้วย
 * · `JSON.stringify(..., null, 2)` ตอนใช้งานจึงได้ JSON ที่ valid เสมอ
 */
const TEMPLATE_DATA = Object.freeze({
	[JSON_LD_TEMPLATE.ITEM_LIST]: {
		'@context': 'https://schema.org',
		'@type': 'ItemList',
		name: 'ชื่อรายการ',
		description: 'คำอธิบายรายการ',
		itemListOrder: 'https://schema.org/ItemListOrderAscending',
		itemListElement: [
			{
				'@type': 'ListItem',
				position: 1,
				item: {
					'@type': 'Thing',
					name: 'ชื่อรายการที่ 1',
					description: 'คำอธิบายรายการที่ 1',
				},
			},
			{
				'@type': 'ListItem',
				position: 2,
				item: {
					'@type': 'Thing',
					name: 'ชื่อรายการที่ 2',
					description: 'คำอธิบายรายการที่ 2',
				},
			},
		],
	},
	[JSON_LD_TEMPLATE.FAQ_PAGE]: {
		'@context': 'https://schema.org',
		'@type': 'FAQPage',
		name: 'คำถามที่พบบ่อย',
		mainEntity: [
			{
				'@type': 'Question',
				name: 'คำถามข้อที่ 1',
				acceptedAnswer: {
					'@type': 'Answer',
					text: 'คำตอบข้อที่ 1',
				},
			},
		],
	},
	[JSON_LD_TEMPLATE.HOW_TO]: {
		'@context': 'https://schema.org',
		'@type': 'HowTo',
		name: 'ชื่อวิธีทำ',
		description: 'คำอธิบายสั้น ๆ',
		step: [
			{
				'@type': 'HowToStep',
				position: 1,
				name: 'ขั้นที่ 1',
				text: 'อธิบายขั้นที่ 1',
			},
		],
	},
	[JSON_LD_TEMPLATE.VIDEO_OBJECT]: {
		'@context': 'https://schema.org',
		'@type': 'VideoObject',
		name: 'ชื่อวิดีโอ',
		description: 'คำอธิบายวิดีโอ',
		thumbnailUrl: 'https://www.studio7thailand.com/path/to/thumbnail.jpg',
		uploadDate: '2026-01-01',
		contentUrl: 'https://www.studio7thailand.com/path/to/video.mp4',
	},
	[JSON_LD_TEMPLATE.EVENT]: {
		'@context': 'https://schema.org',
		'@type': 'Event',
		name: 'ชื่ออีเวนต์',
		startDate: '2026-01-01T10:00',
		endDate: '2026-01-01T18:00',
		eventStatus: 'https://schema.org/EventScheduled',
		location: {
			'@type': 'Place',
			name: 'ชื่อสถานที่',
			address: 'ที่อยู่',
		},
	},
});

export const JSON_LD_TEMPLATE_OPTIONS = [
	{ name: 'รายการ (ItemList) — คอนเทนต์ที่เป็นลิสต์สินค้า/หัวข้อ', value: JSON_LD_TEMPLATE.ITEM_LIST },
	{ name: 'คำถามที่พบบ่อย (FAQPage)', value: JSON_LD_TEMPLATE.FAQ_PAGE },
	{ name: 'ขั้นตอนวิธีทำ (HowTo)', value: JSON_LD_TEMPLATE.HOW_TO },
	{ name: 'วิดีโอ (VideoObject)', value: JSON_LD_TEMPLATE.VIDEO_OBJECT },
	{ name: 'อีเวนต์ (Event)', value: JSON_LD_TEMPLATE.EVENT },
	{ name: 'เขียนเองทั้งหมด (free form)', value: JSON_LD_TEMPLATE.FREE_FORM },
];

/**
 * @param {String} template ค่าจาก JSON_LD_TEMPLATE
 * @returns {String} JSON ที่ format แล้ว — free form คืน string ว่าง
 */
export const getJsonLdTemplateJson = (template) => (
	TEMPLATE_DATA[template]
		? JSON.stringify(TEMPLATE_DATA[template], null, 2)
		: ''
);

export default {};
